import { setDebugState } from "./debug";

export type HeadpatWebConfig = {
  title: string;
  page?: {
    backgroundImage?: string;
    icon?: string;
    credits?: CreditConfig[];
  };
  addons?: {
    ferretPopups?: FerretPopupAddonConfig;
    catCompanions?: CatCompanionAddonConfig;
  };
  headpat?: {
    cursorImage?: string;
    gifs?: string[];
    holdMilliseconds?: number;
    counter?: {
      label?: string;
      initialText?: string;
      getUrl?: string;
      incrementUrl?: string;
      responsePattern?: string;
    };
  };
  model: {
    path: string;
    vtube: string;
    expressions?: Record<string, string>;
  };
  states: {
    default?: string;
    pointerLeave?: string;
    headpat?: string;
  };
};

export type CreditConfig = {
  text?: string;
  before?: string;
  label?: string;
  href?: string;
  after?: string;
  more?: boolean;
};

export type FerretPopupAddonConfig = {
  enabled?: boolean;
  atlas?: string;
  scale?: number;
  minDelayMs?: number;
  maxDelayMs?: number;
  minAnimations?: number;
  maxAnimations?: number;
  horizontalPaddingPx?: number;
  // Distance above the pet layer's bottom, which clears the credits.
  bottomRangePx?: [number, number];
  calmAnimations?: string[];
  calmAnimationRepeats?: [number, number];
  animations?: string[];
  runSpeedPxPerSecond?: number;
  toyAtlas?: string;
  toyChance?: number;
  toys?: FerretToyName[];
  variants?: FerretPopupVariantConfig[];
};

export type FerretToyName = "ball" | "mouse";

export type FerretPopupVariantConfig = {
  id: string;
  label?: string;
  sprite: string;
  minDelayMs?: number;
  maxDelayMs?: number;
};

export type CatToyName = "ball" | "mouse" | "wand" | "fish";

export type CatCompanionAddonConfig = {
  enabled?: boolean;
  atlas?: string;
  scale?: number;
  horizontalPaddingPx?: number;
  // Distance above the pet layer's bottom, which clears the credits.
  bottomRangePx?: [number, number];
  runSpeedPxPerSecond?: number;
  spawnDelayMs?: [number, number];
  minDistancePx?: number;
  toyChance?: number;
  toys?: CatToyName[];
  variants?: CatCompanionVariantConfig[];
};

export type CatCompanionVariantConfig = {
  id: string;
  label?: string;
  sprite: string;
};

export type LoadedVtsModelConfig = {
  app: HeadpatWebConfig;
  modelPath: string;
  modelJsonName: string;
  expressions: ModelExpressionConfig[];
  vtubeData: unknown;
};

export type ModelExpressionConfig = {
  name: string;
  file: string;
};

const defaultConfig: HeadpatWebConfig = {
  title: "Pat the VTuber",
  page: {
    backgroundImage: "/assets/dedahs_background.jpg",
    icon: "/assets/marshmallow.png",
  },
  headpat: {
    cursorImage: "/assets/headpat_cursor_pichuurisu.png",
    gifs: [
      "/assets/headpats_pichuurisu.gif",
      "/assets/headpats_with_hearts_pichuurisu.gif",
    ],
    holdMilliseconds: 500,
    counter: {
      label: "Headpats",
      initialText: "-",
    },
  },
  model: {
    path: "/models/Pyummer",
    vtube: "Pyummer model.vtube.json",
    expressions: {},
  },
  states: {},
};

let configPromise: Promise<LoadedVtsModelConfig> | null = null;

export function loadConfiguredVtsModel(): Promise<LoadedVtsModelConfig> {
  configPromise ??= loadConfig();
  return configPromise;
}

export function setStatus(message: string): void {
  const status = document.getElementById("status");
  if (status) {
    status.textContent = message;
  }
}

async function loadConfig(): Promise<LoadedVtsModelConfig> {
  const app = await fetchJson<HeadpatWebConfig>(
    "/headpat-web-config.json",
    defaultConfig,
  );
  document.title = app.title;

  const modelPath = normalizeDirectory(app.model.path);
  const vtubeData = await fetchJson<Record<string, unknown>>(
    `${modelPath}${app.model.vtube}`,
    {},
  );
  const fileReferences = readRecord(vtubeData.FileReferences);
  const modelJsonName = String(fileReferences.Model ?? "");

  if (!modelJsonName) {
    throw new Error(`No FileReferences.Model in ${modelPath}${app.model.vtube}`);
  }

  setDebugState({ modelPath, modelJsonName });

  return {
    app,
    modelPath,
    modelJsonName,
    expressions: readExpressions(app.model.expressions),
    vtubeData,
  };
}

async function fetchJson<T>(url: string, fallback: T): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    if (response.status === 404) {
      return fallback;
    }

    throw new Error(`Unable to load ${url}: ${response.status}`);
  }

  return (await response.json()) as T;
}

function normalizeDirectory(path: string): string {
  return path.endsWith("/") ? path : `${path}/`;
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function readExpressions(
  expressions: Record<string, string> | undefined,
): ModelExpressionConfig[] {
  return Object.entries(expressions ?? {}).map(([name, file]) => ({
    name,
    file,
  }));
}
